export type FieldErrors = Readonly<Record<string, readonly string[]>>;

export type AppError = Error & {
  readonly name: 'AppError';
  readonly code: string;
  readonly status: number | null;
  readonly fieldErrors: FieldErrors | null;
};

type AppErrorInit = {
  code: string;
  message: string;
  status?: number | null;
  fieldErrors?: FieldErrors | null;
  cause?: unknown;
};

export const createAppError = ({
  code,
  message,
  status = null,
  fieldErrors = null,
  cause,
}: AppErrorInit): AppError =>
  Object.assign(new Error(message, { cause }), {
    name: 'AppError' as const,
    code,
    status,
    fieldErrors,
  });

export const isAppError = (value: unknown): value is AppError =>
  value instanceof Error && value.name === 'AppError' && 'code' in value;

export const toAppError = (value: unknown): AppError =>
  isAppError(value)
    ? value
    : createAppError({
        code: 'UNKNOWN',
        message: value instanceof Error ? value.message : '予期しないエラーが発生しました',
        cause: value,
      });

const MESSAGES: Readonly<Record<string, string>> = {
  NETWORK: 'ネットワークに接続できません。',
  TIMEOUT: '応答がありません。時間をおいて再試行してください。',
  INVALID_RESPONSE: 'データの形式が正しくありません。',
  NOT_FOUND: '該当するデータが見つかりません。',
  NO_CAPACITY: '空きがないため配置できません。',
  ALREADY_ASSIGNED: 'すでにこの案件に配置されています。',
  VALIDATION: '入力内容が正しくありません。',
  INVALID_CREDENTIALS: 'IDまたはパスワードが違います。',
  UNAUTHORIZED: 'ログインが必要です。',
  FORBIDDEN: 'この操作を行う権限がありません。',
  CONFLICT: '同じ内容のデータがすでに存在します。',
};

export const describeError = (error: AppError): string =>
  MESSAGES[error.code] ?? 'エラーが発生しました。';
